import axios, { isAxiosError, isCancel } from 'axios';

type PackingServiceConfig = {
  timeout?: number;
  maxRetryAttempts?: number;
  retryBaseDelay?: number;
  signal?: AbortSignal;
};

export type PackingError = {
  code: string;
  message: string;
  details: Record<string, unknown>;
  retryable: boolean;
};

export type SubmitResponse = {
  jobId: string;
};

export type JobStatusResponse = {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number;
  error?: string;
};

export type JobResultResponse = {
  optimalBox?: {
    name: string;
    packageId?: string;
    length: number;
    width: number;
    height: number;
  };
  itemPositions?: Array<{
    x: number;
    y: number;
    z: number;
    item?: { weight?: number; name?: string };
  }>;
  isMultiBox?: boolean;
  boxes?: Array<{
    box: {
      name: string;
      packageId?: string;
      length: number;
      width: number;
      height: number;
    };
    itemPositions: Array<{
      x: number;
      y: number;
      z: number;
      item?: { weight?: number; name?: string };
    }>;
  }>;
  efficiency?: number;
  algorithm?: string;
  executionTimeMs?: number;
};

const DEFAULT_CONFIG = {
  timeout: 30000,
  maxRetryAttempts: 3,
  retryBaseDelay: 1000,
};

export const PackingErrorCode = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  RATE_LIMITED: 'RATE_LIMITED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  CALCULATION_FAILED: 'CALCULATION_FAILED',
  NO_SOLUTION_FOUND: 'NO_SOLUTION_FOUND',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;

export const isNoSolutionMessage = (message: string): boolean => message.includes('No algorithm succeeded');

const createPackingError = (code: string, message: string, details: Record<string, unknown> = {}, retryable = false): PackingError => ({
  code,
  message,
  details,
  retryable,
});

const isRetryableError = (error: PackingError | Error): boolean => {
  if ('retryable' in error && error.retryable) return true;
  if ('code' in error && error.code === PackingErrorCode.NETWORK_ERROR) return true;
  if ('code' in error && error.code === PackingErrorCode.SERVICE_UNAVAILABLE) return true;
  return false;
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

async function retryWithBackoff<T>(fn: () => Promise<T>, maxAttempts: number, baseDelay: number, attempt = 1): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    const typedError = error as PackingError | Error;

    if (!isRetryableError(typedError) || attempt >= maxAttempts) {
      throw error;
    }

    const retryAfterMs =
      'details' in typedError && typeof (typedError as PackingError).details?.retryAfter === 'number'
        ? ((typedError as PackingError).details.retryAfter as number) * 1000
        : 0;
    const delay = Math.max(baseDelay * 2 ** (attempt - 1), retryAfterMs);
    await sleep(delay);
    return retryWithBackoff(fn, maxAttempts, baseDelay, attempt + 1);
  }
}

const packingClient = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

async function makeRequest<T>(
  url: string,
  options: { method?: string; data?: unknown; timeout: number; signal?: AbortSignal },
): Promise<T> {
  try {
    const response = await packingClient.request<T>({
      url,
      method: options.method ?? 'GET',
      data: options.data,
      timeout: options.timeout,
      signal: options.signal,
    });
    return response.data;
  } catch (error) {
    if (isCancel(error) || (error instanceof Error && error.name === 'CanceledError')) {
      throw error;
    }

    if (isAxiosError(error)) {
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        throw createPackingError(PackingErrorCode.TIMEOUT, 'Request timed out', { timeout: options.timeout }, true);
      }

      if (!error.response) {
        throw createPackingError(
          PackingErrorCode.NETWORK_ERROR,
          'Network error - unable to reach packing service',
          { originalError: error.message },
          true,
        );
      }

      const { status, data: responseData, headers } = error.response;

      if (status === 429) {
        const retryAfter = headers['retry-after'] ?? '60';
        throw createPackingError(
          PackingErrorCode.RATE_LIMITED,
          `Rate limit exceeded. Retry after ${retryAfter} seconds.`,
          { retryAfter: parseInt(String(retryAfter), 10) },
          true,
        );
      }

      if (status === 503) {
        throw createPackingError(PackingErrorCode.SERVICE_UNAVAILABLE, 'Packing service is temporarily unavailable.', {}, true);
      }

      const errorData = (responseData as Record<string, unknown>) ?? {};
      const errorMessage = (errorData.message as string) || (errorData.error as string) || `HTTP ${status}`;
      throw createPackingError(
        isNoSolutionMessage(errorMessage) ? PackingErrorCode.NO_SOLUTION_FOUND : PackingErrorCode.CALCULATION_FAILED,
        errorMessage,
        errorData,
        false,
      );
    }

    const err = error as Error & { code?: string };
    if (err.code && Object.values(PackingErrorCode).includes(err.code as (typeof PackingErrorCode)[keyof typeof PackingErrorCode])) {
      throw error;
    }

    throw createPackingError(PackingErrorCode.NETWORK_ERROR, err.message || 'Unknown error occurred', { originalError: err }, true);
  }
}

const getConfig = (config: PackingServiceConfig = {}) => ({
  ...DEFAULT_CONFIG,
  ...config,
});

export const submitCalculation = async (request: Record<string, unknown>, config: PackingServiceConfig = {}): Promise<SubmitResponse> => {
  const { timeout, maxRetryAttempts, retryBaseDelay, signal } = getConfig(config);

  return retryWithBackoff(
    async () =>
      makeRequest<SubmitResponse>('packing/queue/submit', {
        method: 'POST',
        data: request,
        timeout,
        signal,
      }),
    maxRetryAttempts,
    retryBaseDelay,
  );
};

export const getJobStatus = async (jobId: string, config: PackingServiceConfig = {}): Promise<JobStatusResponse> => {
  const { timeout, maxRetryAttempts, retryBaseDelay, signal } = getConfig(config);
  return retryWithBackoff(
    () => makeRequest<JobStatusResponse>(`packing/queue/status/${jobId}`, { timeout, signal }),
    maxRetryAttempts,
    retryBaseDelay,
  );
};

export const getJobResult = async (jobId: string, config: PackingServiceConfig = {}): Promise<JobResultResponse> => {
  const { timeout, maxRetryAttempts, retryBaseDelay, signal } = getConfig(config);
  return retryWithBackoff(
    () => makeRequest<JobResultResponse>(`packing/queue/result/${jobId}`, { timeout, signal }),
    maxRetryAttempts,
    retryBaseDelay,
  );
};
